import React, {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import ChatsProvider from "./ChatsProvider";
import { ConversationList } from "../../../../model/chats/ChatListModel";
import { navigate } from "../../../../service/navigation/Navigation";
import { appRoutes } from "../../../../routes/routes";
import { ResponsiveDeviceType } from "../../../../widget/layouts/ScreenMedia";
import SessionStorage from "../../../../service/storage/SessionStorage";
import StorageKeys from "../../../../const/StorageKeys";
import UtilityHelper from "../../../../service/utilities/UtilityHelper";
import CacheStorage from "../../../../service/storage/CacheStorage";
import SocketService, {
  socket,
} from "../../../../service/socket/SocketService";

import { useSelector } from "react-redux";
import { RootState } from "../../../../libraries/state_reducers/store";
import { useDispatch } from "react-redux";
import {
  chatRefreshListStatus,
  clearResetSelectedChat,
} from "../../../../libraries/state_reducers/chats/ChatsReducer";
import ShowToast from "../../../../widget/common/ui/Toast";
import AppTranslation from "../../../../service/app_translation/AppTranslation";
import Loader from "../../../../widget/common/ui/Loader";
import { AlertMesage } from "../../../../widget/common/ui/Alerts";
import { removeModal } from "../../../../libraries/state_reducers/widget/ModalsReducer";
import ModalKeys from "../../../../const/ModalKeys";

type ChatsContextType = {
  chats: Array<ConversationList> | null;
  chatsOrig: Array<ConversationList> | null;
  chatSelected: ConversationList | null;

  socketStatus: boolean;
  userId: string;
  showMediaOption: boolean;
  loadingMesages: boolean;

  showSidebar: ShowSidebarType;
  sidebarShowHide: (status: boolean, title: string) => void;

  getChatListAPI: () => void;
  getSelectedChatSession: () => void;
  toggleMediaOption: (status: boolean | null) => void;

  handleClickChat: (detail: ConversationList) => void;

  handleTypingMessage: (flag: "1" | "2", status: boolean) => void; // 1-text, 2-audio
  stopAllAudio: () => void;

  // SEACH CHAT
  searchValue: string;
  searchHandleChange: (name: string, value: string) => void;
  searchClear: () => void;

  // CHAT REPORT
  maxWords: number;
  submitChatReport: () => void;
  reportChangeInput: (name: string, value: string) => void;
  reportMessage: string;

  // SOCKET
  socketConnect: () => void;
};

type ShowSidebarType = {
  status: boolean;
  title: string;
};

const ChatsContext = createContext<ChatsContextType | undefined>(undefined);

export const ChatsContextProvider = ({ children }: { children: ReactNode }) => {
  const type: number = ResponsiveDeviceType();
  const dispatch = useDispatch();

  const chatListAbortControllerRef = useRef<AbortController>();

  const chatRefreshListStatusSelector = useSelector(
    (state: RootState) => state.chats.chatRefreshListStatus
  );

  const chatClearResetChatDetailSelector = useSelector(
    (state: RootState) => state.chats.chatClearResetChatDetail
  );

  const maxWords: number = 2000;

  const isConnectedRef = useRef<boolean>(false);
  const searchValueRef = useRef<string>("");
  const chatsRef = useRef<Array<ConversationList> | null>(null);
  const chatSelectedRef = useRef<ConversationList | null>(null);

  const userId = CacheStorage.getValue(StorageKeys.account_id) || "0";

  const [searchValue, setSearchValue] = useState<string>("");

  const [socketStatus, setSocketStatus] = useState<boolean>(false);

  const [loadingMesages, setLoadingMesages] = useState<boolean>(false);
  const [showMediaOption, setShowMediaOption] = useState<boolean>(false);
  const [chats, setChats] = useState<Array<ConversationList> | null>(null);
  const [chatsOrig, setChatsOrig] = useState<Array<ConversationList> | null>(
    null
  );
  const [chatSelected, setChatSelected] = useState<ConversationList | null>(
    null
  );

  const [reportMessage, setReportMessage] = useState<string>("");

  const showSidebarDefault: ShowSidebarType = {
    status: false,
    title: "",
  };
  const [showSidebar, setShowSidebar] =
    useState<ShowSidebarType>(showSidebarDefault);

  // RESET FROM /////////////////////////////////////////////////
  const resetValues = () => {
    const chatsSession = SessionStorage.getValue(StorageKeys.chats_chat_list);
    let chatList: Array<ConversationList> | null = null;

    if (UtilityHelper.isNotEmpty(chatsSession)) {
      chatList = JSON.parse(chatsSession);
    }

    setChats(chatList);
    setChatsOrig(chatList);

    setShowMediaOption(false);
    setLoadingMesages(false);

    chatsRef.current = null;
    chatSelectedRef.current = null;
    isConnectedRef.current = false;

    setSearchValue("");
    searchValueRef.current = "";

    setReportMessage("");
    setShowSidebar(showSidebarDefault);
  };

  // TOGGLE MEDIA OPTION /////////////////////////////////////////////////
  const toggleMediaOption = (status: boolean | null) => {
    if (status != null) {
      setShowMediaOption(status);
    } else {
      setShowMediaOption(!showMediaOption);
    }
  };

  // GETTING CONVERSATION /////////////////////////////////////////////////
  const getChatListAPI = async () => {
    if (chatListAbortControllerRef.current) {
      chatListAbortControllerRef.current.abort();
    }

    chatListAbortControllerRef.current = new AbortController();
    const signal = chatListAbortControllerRef.current.signal;

    const conversationList: Array<ConversationList> | null =
      await ChatsProvider.getConversationsAPI(signal);

    if (conversationList != null) {
      console.log("getChatListAPI");
      setChats(conversationList);
      setChatsOrig(conversationList);
      chatsRef.current = conversationList;

      SessionStorage.saveValue(
        StorageKeys.chats_chat_list,
        JSON.stringify(conversationList)
      );
    }

    connectFromPopup();
  };

  const connectFromPopup = () => {
    const chatsPopupConnectStatus =
      SessionStorage.getValue(StorageKeys.chats_popup_connect) || "0";

    if (chatsPopupConnectStatus === "1") {
      const hasSelectedChatSession: boolean = getSelectedChatSession();
      if (hasSelectedChatSession) {
        addChatList(chatSelectedRef.current!);
        handleClickChat(chatSelectedRef.current!, false);
      }

      SessionStorage.deleteValue(StorageKeys.chats_popup_connect);
    }
  };

  const addChatList = (detail: ConversationList | null) => {
    if (detail != null) {
      console.log(`addChatList`);
      const exists: boolean | undefined = chatsRef.current?.some(
        (chat) => chat.conv_id === detail?.conv_id
      );
      console.log(`addChatList: ${exists}`);

      if (!exists || exists === undefined) {
        console.log(`addChatList aaa: ${exists}`);
        let conversationList: Array<ConversationList> | null =
          chatsRef.current!;
        conversationList = [detail, ...conversationList];

        setChats(conversationList);
        setChatsOrig(conversationList);
        chatsRef.current = conversationList;

        SessionStorage.saveValue(
          StorageKeys.chats_chat_list,
          JSON.stringify(conversationList)
        );
      }
    }
  };

  // CLICK CONVERSATION PORFILE /////////////////////////////////////////////////
  const handleClickChat = async (
    detail: ConversationList,
    redirect: boolean = true
  ) => {
    getSelectedChatSession();
    setShowSidebar(showSidebarDefault);

    let isSameChat: boolean = false;
    if (Object.keys(chatSelectedRef.current ?? {}).length > 0 && type === 3) {
      isSameChat = chatSelectedRef.current?.conv_id! === detail?.conv_id!;
    }

    if (!isSameChat) {
      /* if (isConnectedRef.current) {
        if (type === 3) {
          SocketService.leaveChat();
        }
      } */

      SessionStorage.saveValue(
        StorageKeys.chats_chat_selected,
        JSON.stringify(detail)
      );

      setChatSelected(detail);
      chatSelectedRef.current = detail;

      if (redirect) {
        if (type === 3) {
          setLoadingMesages(true);
          await UtilityHelper.delay();
          setLoadingMesages(false);
        } else {
          if (["1", "4"].includes(detail?.flag!)) {
            navigate(appRoutes.routeNameChatTeam);
          } else if (detail?.flag! === "2") {
            navigate(appRoutes.routeNameChatDepartment);
          } else if (detail?.flag! === "3") {
            navigate(appRoutes.routeNameChatGroup);
          }
        }
      }
    }
  };

  // GET SESSION SELECTED CHAT -> getting messages /////////////////////////////////////////////////
  const getSelectedChatSession = (): boolean => {
    console.log(`getSelectedChatSession`);
    const chatsChatSelectedSession = SessionStorage.getValue(
      StorageKeys.chats_chat_selected
    );

    console.log(chatsChatSelectedSession);

    if (UtilityHelper.isNotEmpty(chatsChatSelectedSession)) {
      const chatSelectedSession: ConversationList = JSON.parse(
        chatsChatSelectedSession
      );

      chatSelectedRef.current = chatSelectedSession;
      setChatSelected(chatSelectedSession);

      setChatReadStatus(chatSelectedSession);

      return true;
    }

    return false;
  };

  // SET READ STATUS - WEB ONLY /////////////////////////////////////////////////
  const setChatReadStatus = (detail: ConversationList) => {
    console.log(`setChatReadStatus`);
    if ((chatsRef.current ?? []).length > 0) {
      let chatListTem: ConversationList[] | null = chatsRef.current;

      chatListTem?.forEach((item) => {
        if (item.conv_id === detail.conv_id) {
          item.unread_count = "0";
        }
      });

      chatsRef.current = chatListTem;
      setChats(chatListTem);
    }
  };

  // CHAT REPORT  /////////////////////////////////////////////////////////////////////////////
  const submitChatReport = async () => {
    if (UtilityHelper.isEmpty(reportMessage)) {
      ShowToast.warning(AppTranslation.text("message_cannot_be_empty"));
    } else {
      if (UtilityHelper.countWords(reportMessage) > maxWords) {
        ShowToast.warning(AppTranslation.text("character_exceeded"));
      } else {
        // GETTING CHAT DETAIL
        getSelectedChatSession();

        Loader.modal();
        const statusResponse = await ChatsProvider.reportChatAPI({
          conversId: chatSelectedRef.current?.conv_id!,
          message: reportMessage,
        });
        Loader.modal(false);

        if (statusResponse) {
          AlertMesage({
            title: "report_success",
            onClick: () => {
              dispatch(removeModal(ModalKeys.chat_report));
            },
          });
        }
      }
    }
  };

  const reportChangeInput = (fieldName: string, fieldValue: string) => {
    fieldName = fieldName.toLowerCase();
    if (fieldName === `chat-report-message`) {
      setReportMessage(fieldValue);
    }
  };

  // SOCKET CONNECT /////////////////////////////////////////////////////////////////////////////
  const socketConnect = async (): Promise<boolean> => {
    const connectStatus: boolean = await SocketService.initialize();

    await UtilityHelper.delay();
    setSocketStatus(connectStatus);
    isConnectedRef.current = connectStatus;

    /* if (connectStatus) {
      const segments: Array<string> = urlLocation.pathname.split("/");
      const pathName: string = segments[segments.length - 1];
      if (
        pathName.toLowerCase() ==
        routeNameChats.replaceAll("/", "").toLowerCase()
      ) {
        SocketService.leaveChat();
      }
    } */

    return connectStatus;
  };

  /// MESSAGES  //////////////////////////////////////////////////////////////////////////
  // 1-text, 2-audio
  const handleTypingMessage = (flag: "1" | "2", status: boolean = true) => {
    getSelectedChatSession();
    SocketService.typingChatMessage({
      flag: flag, //chatSelectedRef.current?.flag!,
      convId: chatSelectedRef.current?.conv_id!,
      status: status ? "1" : "0",
    });
  };

  const stopAllAudio = () => {
    ChatsProvider.stopAllAudio();
  };

  /// SEACH CHAT  //////////////////////////////////////////////////////////////////////////
  const searchClear = () => {
    setChats(chatsRef.current);
    setSearchValue("");
    searchValueRef.current = "";
  };

  const searchHandleChange = (fieldName: string, fieldValue: string) => {
    fieldName = fieldName.toLowerCase();
    if (fieldName === `search-profile-onversation`) {
      setSearchValue(fieldValue);
      searchValueRef.current = fieldValue;

      const filtered: Array<ConversationList> | undefined =
        chatsRef.current?.filter((detail) =>
          Object.values(detail)
            .join(" ")
            .toLowerCase()
            .includes(searchValueRef.current.toLowerCase())
        );

      setChats(filtered!);
    }
  };

  const sidebarShowHide = (status: boolean, title: string) => {
    setShowSidebar({ status: status, title: title });
  };

  const reloadChatList = async () => {
    //setShowSidebar({ status: false, title: "" });

    let connected: boolean = socket.connected;
    if (!socket.connected) {
      Loader.modal();
      connected = await socketConnect();
      Loader.modal(false);
    }

    console.log(`reloadChatList.... connected: ${connected}`);
    if (connected) {
      getChatListAPI();
    }
  };

  /// USE EFFECTS  //////////////////////////////////////////////////////////////////////////
  useEffect(() => {
    resetValues();
    socketConnect();

    // COMING FROM CONNECT POPUP
    //connectFromPopup();

    return () => {
      if (socket.connected) {
        SocketService.disconnect();
        console.log(`socket disconnect - ChatContextProvider`);
      }
    };
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (chatRefreshListStatusSelector) {
      console.log("reload chatlist");
      if (type === 3) {
        reloadChatList();
      }

      dispatch(chatRefreshListStatus(false));
    }
    // eslint-disable-next-line
  }, [chatRefreshListStatusSelector]);

  useEffect(() => {
    if (chatClearResetChatDetailSelector) {
      resetValues();
      dispatch(clearResetSelectedChat(false));
    }
    // eslint-disable-next-line
  }, [chatClearResetChatDetailSelector]);

  /// RETRUN  //////////////////////////////////////////////////////////////////////////

  return (
    <ChatsContext.Provider
      value={{
        chats,
        chatsOrig,
        chatSelected,
        userId,
        showMediaOption,
        socketStatus,
        loadingMesages,

        showSidebar,
        sidebarShowHide,

        getSelectedChatSession,
        getChatListAPI,
        toggleMediaOption,
        handleClickChat,

        stopAllAudio,

        // SEACH CHAT
        searchValue,
        searchHandleChange,
        searchClear,

        // CHAT REPORT
        maxWords,
        submitChatReport,
        reportChangeInput,
        reportMessage,

        handleTypingMessage,

        // SOCKET
        socketConnect,
      }}
    >
      {children}
    </ChatsContext.Provider>
  );
};

// IMPORT / USE CONTEXT ///////////////////////////////////////////////////////////////////
export const ChatsContextImport = () => {
  const context = useContext(ChatsContext);
  if (!context) {
    throw new Error(
      "ChatsContextImport must be used within a ChatsContextProvider"
    );
  }
  return context;
};
